import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { roleCanEditCatalog, type PublicTote, type ToteListResponse } from '@inventohub/shared';
import { ApiClientError, apiDownload, apiRequest, triggerDownload } from '../lib/api';
import { useAuth } from '../lib/auth';

export function LabelsPage(): JSX.Element {
  const { user } = useAuth();
  const canEdit = user ? roleCanEditCatalog(user.role) : false;
  const [totes, setTotes] = useState<PublicTote[]>([]);
  const [toteId, setToteId] = useState('');
  const [serials, setSerials] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<ToteListResponse>('/api/totes');
        if (!cancelled) setTotes(body.totes);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load totes.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!canEdit) {
    return (
      <section className="card" data-testid="labels-page">
        <h2>QR labels</h2>
        <p className="status status--error">Only an admin or equipment admin can print labels.</p>
      </section>
    );
  }

  async function printTote(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      const { blob, filename } = await apiDownload('/api/labels', {
        method: 'POST',
        body: { toteId },
      });
      triggerDownload(blob, filename);
      setNotice('Tote label downloaded. Reprinting does not change the tote identity.');
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not print the tote label.',
      );
    }
  }

  async function printAssets(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    const assetSerials = serials
      .split(/[\s,]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);
    try {
      const { blob, filename } = await apiDownload('/api/labels', {
        method: 'POST',
        body: { assetSerials },
      });
      triggerDownload(blob, filename);
      setNotice('Asset labels downloaded. Reprinting does not mint a new identity.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not print asset labels.');
    }
  }

  return (
    <section data-testid="labels-page">
      <div className="card">
        <h2>QR labels</h2>
        <p className="meta">
          Labels encode https://inventohub.ewpok.com and the human tote number or asset serial.
          Never print production stickers from a preview URL.
        </p>
        {error ? <p className="status status--error">{error}</p> : null}
        {notice ? <p className="status status--ok">{notice}</p> : null}
      </div>
      <form className="card" onSubmit={(event) => void printTote(event)}>
        <h2>Tote label</h2>
        <label className="field">
          Tote
          <select value={toteId} onChange={(event) => setToteId(event.target.value)} required>
            <option value="">Select…</option>
            {totes.map((tote) => (
              <option key={tote.id} value={tote.id}>
                {tote.number}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="button">
          Print tote QR
        </button>
      </form>
      <form className="card" onSubmit={(event) => void printAssets(event)}>
        <h2>Asset labels</h2>
        <label className="field">
          Serials (comma or line separated)
          <textarea
            value={serials}
            onChange={(event) => setSerials(event.target.value)}
            required
            data-testid="label-serials"
          />
        </label>
        <button type="submit" className="button">
          Print asset QRs
        </button>
      </form>
    </section>
  );
}
